const root = document.querySelector('[data-quick-view-root]');
const dialog = root?.querySelector('[data-quick-view-dialog]');
const content = root?.querySelector('[data-quick-view-content]');
const template = root?.querySelector('[data-quick-view-template]');
const loadingLabel = root?.querySelector('[data-quick-view-loading]')?.textContent || '';
let trigger = null;
let controller = null;
let closeTimer = null;

const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
  style: 'currency', currency
}).format(cents / 100);

const wait = (delay, signal) => new Promise((resolve, reject) => {
  const timer = window.setTimeout(resolve, delay);
  signal.addEventListener('abort', () => {
    window.clearTimeout(timer);
    reject(new DOMException('Aborted', 'AbortError'));
  }, { once: true });
});

async function fetchProduct(handle, signal) {
  const url = `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}.js`;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    if (response.ok) return response.json();
    if (attempt === 0 && response.status >= 429) await wait(350, signal);
    else throw new Error('Product unavailable');
  }
  throw new Error('Product unavailable');
}

function finishClose(restoreFocus = true) {
  window.clearTimeout(closeTimer);
  controller?.abort();
  if (dialog?.open) dialog.close();
  dialog?.removeAttribute('data-closing');
  document.documentElement.style.overflow = '';
  if (restoreFocus) trigger?.focus();
}

function close(restoreFocus = true, immediate = false) {
  if (!dialog?.open) return;
  if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    finishClose(restoreFocus);
    return;
  }
  dialog.dataset.closing = '';
  closeTimer = window.setTimeout(() => finishClose(restoreFocus), 220);
}

async function open(button) {
  if (!dialog || !content || !template) return;
  trigger = button;
  controller?.abort();
  controller = new AbortController();
  content.innerHTML = `<div class="quick-view__loading">${loadingLabel}</div>`;
  dialog.removeAttribute('data-closing');
  dialog.showModal();
  document.documentElement.style.overflow = 'hidden';
  try {
    const product = await fetchProduct(button.dataset.productHandle, controller.signal);
    const fragment = template.content.cloneNode(true);
    const image = fragment.querySelector('[data-quick-view-image]');
    const variants = fragment.querySelector('[data-quick-view-variants]');
    const price = fragment.querySelector('[data-quick-view-price]');
    const add = fragment.querySelector('[data-quick-view-add]');
    const requestedVariantId = String(button.dataset.variantId || '');
    fragment.querySelector('[data-quick-view-title]').textContent = product.title;
    const detailsUrl = new URL(
      `${window.Shopify.routes.root}products/${encodeURIComponent(button.dataset.productHandle)}`,
      window.location.origin
    );
    if (requestedVariantId) detailsUrl.searchParams.set('variant', requestedVariantId);
    fragment.querySelector('[data-quick-view-link]').href = `${detailsUrl.pathname}${detailsUrl.search}`;
    image.alt = product.title;
    product.variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.title;
      option.disabled = !variant.available;
      option.dataset.price = variant.price;
      option.dataset.available = variant.available;
      variants.append(option);
    });
    if (requestedVariantId && variants.querySelector(`option[value="${CSS.escape(requestedVariantId)}"]`)) {
      variants.value = requestedVariantId;
    }
    const update = () => {
      const selected = variants.selectedOptions[0];
      const selectedVariant = product.variants.find((variant) => String(variant.id) === selected.value);
      image.src = selectedVariant?.featured_image?.src || product.featured_image || product.images?.[0] || '';
      price.textContent = money(Number(selected.dataset.price), product.currency || window.Shopify?.currency?.active || 'EUR');
      const available = selected.dataset.available === 'true';
      add.disabled = !available;
      add.textContent = available ? root.dataset.addToCart : root.dataset.soldOut;
    };
    variants.addEventListener('change', update);
    content.replaceChildren(fragment);
    update();
    content.querySelector('select')?.focus({ preventScroll: true });
  } catch (error) {
    if (error.name !== 'AbortError') content.textContent = root.dataset.loadError;
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-quick-view-trigger]');
  if (button) { event.preventDefault(); open(button); return; }
  if (event.target.closest('[data-quick-view-close]') || event.target === dialog) close();
});
document.addEventListener('submit', (event) => {
  if (!event.target.matches('[data-quick-view-form]')) return;
  // This capture-phase handler runs before the cart Canvas submit handler.
  // Native dialog elements cannot be stacked reliably, so close immediately
  // and let the cart become the only active modal.
  close(false, true);
}, true);
dialog?.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
